import { OrderClient } from "@types";
import * as yup from "yup";

const normalizeFloorValue = (floor: OrderClient["floor"] | null | undefined) =>
  typeof floor === "number" && Number.isFinite(floor) ? floor : "";

const floorValidation = yup
  .number()
  .transform((value, originalValue) => {
    if (originalValue === "" || originalValue == null) {
      return undefined;
    }

    return typeof originalValue === "number"
      ? originalValue
      : Number(originalValue);
  })
  .typeError("L'étage doit être un nombre.")
  .required("L'étage est requis.");

export interface ClientInformationValidationValues {
  firstname: string;
  lastname: string;
  address: string;
  address_addition: string;
  postal_code_city: string;
  floor: number | string;
  email: string;
  phone: string;
}

export const hasExistingGpsCoordinates = (
  client: Pick<OrderClient, "lat" | "lng">,
) => {
  return (
    typeof client.lat === "number" &&
    typeof client.lng === "number" &&
    !isNaN(client.lat) &&
    !isNaN(client.lng)
  );
};

export const getClientInformationValidationValues = (
  client: Pick<
    OrderClient,
    | "firstname"
    | "lastname"
    | "address"
    | "address_addition"
    | "postal_code"
    | "city"
    | "floor"
    | "email"
    | "phone"
  >,
): ClientInformationValidationValues => {
  return {
    firstname: client.firstname || "",
    lastname: client.lastname || "",
    address: client.address || "",
    address_addition: client.address_addition || "",
    postal_code_city:
      client.postal_code || client.city
        ? `${client.postal_code || ""} - ${client.city || ""}`.trim()
        : "",
    floor: normalizeFloorValue(client.floor),
    email: client.email || "",
    phone: client.phone || "",
  };
};

/**
 * Creates a validation schema for the client information form.
 * @param hasExistingGps - Whether the client already has lat/lng coordinates in the database.
 *                         If true, the address field validation is relaxed for backwards compatibility.
 */
export const createValidationSchema = (hasExistingGps: boolean) =>
  yup.object({
    lastname: yup.string().required("Le nom est requis."),
    firstname: yup.string().required("Le prénom est requis."),
    address: hasExistingGps
      ? yup.string().notRequired()
      : yup.string().required("L'adresse de livraison est requise."),
    postal_code_city: yup
      .string()
      .nullable()
      .required("Le code postal et la ville sont requis."),
    floor: floorValidation,
    phone: yup.string().required("Le numéro de téléphone est requis."),
    email: yup
      .string()
      .email("Veuillez entrer une adresse email valide.")
      .required("L'adresse email est requise."),
  });

export const isClientInformationComplete = (client: OrderClient) => {
  const hasExistingGps = hasExistingGpsCoordinates(client);
  const values = getClientInformationValidationValues(client);

  return createValidationSchema(hasExistingGps).isValidSync(values);
};

// Default export for backwards compatibility (new orders without existing GPS)
const validationSchema = createValidationSchema(false);

export default validationSchema;
